<template>
  <el-dialog
    :visible="true"
    :close-on-click-modal="false"
    :before-close="handleClose"
    :fullscreen="true"
  >
    <div slot="title">
      {{ title }}
      <el-tag
        v-if="inwardHandling.status && type === 'update' && !loading"
        size="small"
        :type="getStatus().tag"
        style="margin-left: 5px;"
      >
        {{ getStatus().label }}
      </el-tag>
    </div>
    <div v-if="type === 'view' && inwardHandling.receipt_number">
      <el-link @click="openReceipt" type="primary" style="margin-bottom: 30px; margin-top: -20px;">
        <i class="el-icon-printer el-icon--right"></i>
        {{ inwardHandling.receipt_number }}
      </el-link>
    </div>
    <div v-loading="loading">
      <el-form
        ref="orderForm"
        label-position="top"
        :model="inwardHandling"
        :rules="rules"
      >
        <el-switch
          v-model="hasSupplierOrder"
          active-text="Bevételezés beszállítói megrendelés alapján"
          style="margin-bottom: 30px;"
          @change="onHasSupplierOrderChange"
          :disabled="inwardHandling.status !== 'recorded' || type === 'view'"
        />
        <el-row :gutter="20">
          <el-col :span="hasSupplierOrder ? 6 : 8">
            <el-form-item
              prop="supplier"
              label="Szállító"
            >
              <el-select
                v-if="hasSupplierOrder"
                v-model="inwardHandling.supplier"
                style="width: 100%;"
                value-key="id"
                :disabled="inwardHandling.status !== 'recorded' || type === 'view'"
                @change="onSupplierChange"
              >
                <el-option
                  v-for="supplier in suppliers"
                  :key="supplier.id + 'supplier'"
                  :value="supplier"
                  :label="supplier.company.name"
                />
              </el-select>
              <el-select
                v-if="!hasSupplierOrder"
                v-model="inwardHandling.supplier"
                style="width: 100%;"
                remote
                filterable
                :remote-method="searchSuppliers"
                :loading="remoteLoading"
                value-key="id"
                :disabled="inwardHandling.status !== 'recorded' || type === 'view'"
                @change="onSupplierChange"
              >
                <el-option
                  v-for="supplier in remoteSuppliers"
                  v-show="supplier.is_supplier"
                  :key="supplier.id + 'supplier'"
                  :value="supplier"
                  :label="supplier.company.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col
            v-if="hasSupplierOrder"
            :span="6"
          >
            <el-form-item
              prop="supplier_order"
              label="Beszállítói megrendelés"
            >
              <el-select
                v-model="inwardHandling.supplier_order"
                style="width: 100%;"
                value-key="id"
                :disabled="Object.keys(inwardHandling.supplier).length === 0 && inwardHandling.supplier.constructor === Object || inwardHandling.status !== 'recorded' || type === 'view'"
                @change="onSelectOrder"
              >
                <el-option
                  v-for="order in inwardHandling.supplier.supplier_orders"
                  :key="order.id"
                  :label="'#' + order.id +' ' + order.receipt_number"
                  :value="order"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="hasSupplierOrder ? 6 : 8">
            <el-form-item
              prop="customer"
              label="Átvevő"
            >
              <el-select
                v-model="inwardHandling.customer"
                style="width: 100%;"
                value-key="id"
                filterable
                :disabled="inwardHandling.status !== 'recorded' || hasSupplierOrder || type === 'view'"
              >
                <el-option
                  v-for="customer in customers"
                  :key="customer.id + 'supplier'"
                  :value="customer"
                  :label="customer.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="hasSupplierOrder ? 6 : 8">
            <el-form-item
              prop="storage"
            >
              <div
                slot="label"
                style="display: inline-block;"
              >
                Raktár
                <template v-if="$config('SHOW_STORAGE_TOOLTIPS')">
                <el-tooltip
                  class="item"
                  effect="dark"
                  :content="`Ebbe a raktárba fog érkezni a szállítmany. ${$config('STORAGE_TOOLTIP_SUFFIX')}`"
                  placement="right"
                >
                  <i class="el-icon-info"/>
                </el-tooltip>
                </template>
              </div>
              <el-select
                v-model="inwardHandling.storage"
                style="width: 100%;"
                value-key="id"
                :disabled="inwardHandling.status !== 'recorded' || hasSupplierOrder || type === 'view'"
              >
                <el-option
                  v-for="storage in storages"
                  :key="storage.id + 'storage'"
                  :value="storage"
                  :label="storage.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item
              prop="receipt_prefix"
              label="Bizonylat előtag"
            >
              <el-input
                v-model="receiptPrefix"
                disabled
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="currency_id"
              label="Deviza"
            >
              <el-select
                v-model="inwardHandling.currency"
                :disabled="inwardHandling.status !== 'recorded' || type === 'view'"
                style="width: 100%;"
                value-key="id"
                filterable
              >
                <el-option
                  v-for="currency in currencies"
                  :key="currency.id + 'currency'"
                  :value="currency"
                  :label="currency.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="completed_at"
              label="Teljesítés dátuma"
            >
              <el-date-picker
                v-model="inwardHandling.completed_at"
                type="date"
                :disabled="inwardHandling.status === 'done' || type === 'view'"
                style="width: 100%;"
                :clearable="false"
                format="yyyy. MM. dd."
                value-format="yyyy-MM-dd"
                :picker-options="{ firstDayOfWeek: 1 }"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="reference_number"
            >
              <div
                slot="label"
                style="display: inline-block;"
              >
                Hivatkozási szám
                <el-tooltip
                  class="item"
                  effect="dark"
                  content="Szállítólevél-, számlaszám, vagy CMR azonosító."
                  placement="right"
                >
                  <i class="el-icon-info"/>
                </el-tooltip>
              </div>
              <el-input
                v-model="inwardHandling.reference_number"
                :disabled="inwardHandling.status === 'done' || type === 'view'"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item
              prop="cm_delivery_note_number"
              label="CM szlev száma"
            >
              <el-input
                v-model="inwardHandling.cm_delivery_note_number"
                :disabled="type === 'update'"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item
              prop="comment"
              label="Megjegyzés"
            >
              <el-input
                v-model="inwardHandling.comment"
                type="textarea"
                :rows="2"
                style="word-break: normal;"
                :disabled="type === 'view'"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="is_stockist"
              style="margin-top: 22px;"
            >
              <el-checkbox
                v-model="inwardHandling.is_stockist"
                :disabled="type === 'view'"
              >
                Beszerzési árak véglegesek
              </el-checkbox>
            </el-form-item>
          </el-col>
        </el-row>
        <product-items
          ref="productItems"
          v-loading="productItemsLoading"
          :is-product-select-from-stock="false"
          :load-base-count-from-stock="false"
          :enabled-columns="type === 'view' ? ['product', 'productPackType', 'expirationDate', 'supplierSelector', 'count', 'unitPrice', 'netPrice', 'vat'] : ['product', 'productPackType', 'expirationDate', 'supplierSelector', 'count', 'unitPrice', 'netPrice', 'vat', 'actions']"
          :currency="inwardHandling.currency && inwardHandling.currency.code || 'HUF'"
          :default-supplier="type === 'add' && inwardHandling.supplier || null"
          :default-items="type === 'add' && inwardHandling.remaining_inward_handlings || inwardHandling.inward_handling_items"
          :available-products="products"
          :enabled-elements="enabledElements"
          :min-count="hasSupplierOrder ? 0 : 1"
          :is-vat-zero="inwardHandling.supplier && inwardHandling.supplier.supplier_category && inwardHandling.supplier.supplier_category.value === 'Külföldi szállító'"
          @priceChange="onPriceChange"
        />
      </el-form>
      <div class="price-sum">
        <div>Nettó: <strong>{{ inwardHandling.net_price | currency(inwardHandling.currency && inwardHandling.currency.code || 'HUF') }}</strong></div>
        <div style="margin-left: 20px;">
          Bruttó: <strong>{{ inwardHandling.gross_price | currency(inwardHandling.currency && inwardHandling.currency.code || 'HUF') }}</strong>
        </div>
      </div>
    </div>
    <div
      v-if="type !== 'view'"
      slot="footer"
      class="dialog-footer"
    >
      <el-button
        v-if="inwardHandling.status !== 'recorded' && inwardHandling.status !== 'cancelled' && inwardHandling.receipt_file"
        @click="openReceipt"
      >
        Bizonylat nyomtatása
      </el-button>
      <el-button
        type="primary"
        :loading="buttonLoading"
        @click="store"
      >
        Mentés
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import moment from 'moment';
import ProductItems from '../../utils/ProductItems/index';

export default {
  components: {
    ProductItems,
  },
  props: ['type', 'inwardHandlingId'],
  data() {
    return {
      hasSupplierOrder: true,
      receiptPrefix: '',
      orders: [],
      products: null,
      remoteLoading: false,
      remoteSuppliers: [],
      suppliers: [],
      supplierSelectKey: 0,
      supplierOrderLoading: false,
      productItemsLoading: false,
      loading: false,
      buttonLoading: false,
      cancelButtonLoading: false,
      restoreButtonLoading: false,
      sendButtonLoading: false,
      inwardHandling: {
        completed_at: moment()
        .format('Y-MM-DD'),
        currency_id: 0,
        status: 'recorded',
        net_price: 0,
        gross_price: 0,
        order_items: [],
        storage: {},
        supplier: {},
        customer: {},
        currency: {},
        has_no_more_delivery: false,
        cm_delivery_note_number: '',
      },
      rules: {
        currency_id: [
          {
            required: true,
            message: 'Mező kitöltése kötelező',
            trigger: 'change'
          }
        ],
        completed_at: [
          {
            required: true,
            message: 'Mező kitöltése kötelező',
            trigger: 'change'
          }
        ],
        reference_number: [
          {
            required: true,
            message: 'Mező kitöltése kötelező',
            trigger: 'change'
          }
        ],
        customer: [
          {
            required: true,
            message: 'Vevő kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        supplier: [
          {
            required: true,
            message: 'Beszállító kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        storage: [
          {
            required: true,
            message: 'Raktár kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        cm_delivery_note_number: [
          {
            max: 255,
            message: 'A CM szállítólevél szám maximális hossza 255 karakter',
            trigger: 'blur'
          }
        ]
      },
    };
  },
  computed: {
    title() {
      if (this.type === 'add') {
        return 'Új bevételezés';
      } else if (this.type === 'update') {
        return 'Bevételezés módosítása';
      }else if (this.type === 'view') {
        return 'Bevételezés megtekintése';
      } else {
        return '';
      }
    },
    buttonText() {
      if (this.type === 'add') {
        return 'Hozzáadása';
      } else if (this.type === 'update') {
        return 'Módosítás';
      } else {
        return '';
      }
    },
    enabledElements() {
      if(this.type === 'view') {
        return  [];
      }
      switch (this.inwardHandling.status) {
        case 'recorded':
          return ['addButton', 'deleteButton', 'product', 'pack', 'count', 'netPrice', 'vat', 'unitPrice', 'expirationDate', 'supplierSelector'];
        case 'done':
          return this.inwardHandling.is_stockist ? [] : ['unitPrice', 'netPrice'];
        default:
          return [];
      }
    },
    customers() {
      return this.$store.getters['ownCompany/ownCompanies'];
    },
    storages() {
      return this.$store.getters['storage/ownStorages'];
    },
    currencies() {
      return this.$store.getters['currency/currencies']
    },

  },
  async mounted() {
    this.loading = true;
    const requests = [];
    if (!this.customers || !this.customers.length) {
      requests.push(this.$store.dispatch('ownCompany/fetchAll'));
    }
    if (!this.storages || !this.storages.length) {
      requests.push(this.$store.dispatch('storage/fetchAll'));
    }
    if (!this.currencies || !this.currencies.length) {
      requests.push(this.$store.dispatch('currency/fetchAll'));
    }
    requests.push(this.$store.dispatch('partner/fetchSuppliers'));
    await Promise.all(requests);
    this.suppliers = this.$store.getters['partner/suppliers'].filter(s => !!s.supplier_orders.find(so => so.status === 'sent' || so.status === 'partly-done' && !so.has_no_more_delivery));

    this.receiptPrefix = (await axios.get('api/receipt-prefixes/inwardHandling')).full_prefix;
    if (this.inwardHandlingId) {
      this.inwardHandling = await this.$store.dispatch('inwardHandling/fetch', this.inwardHandlingId);
      if (this.inwardHandling.status === 'done') {
        this.suppliers = this.$store.getters['partner/suppliers'];
      }
      if (this.inwardHandling.supplier_order) {
        this.inwardHandling.remaining_inward_handlings = (await axios.get('api/supplier-orders/remaining-inward-items/' + this.inwardHandling.supplier_order.id)).map(item => ({
          ...item,
          max_count: item.count,
          key: Math.random().toString(36).substring(7),
        }));
        this.orders.push(this.inwardHandling.supplier_order);
      } else {
        this.remoteSuppliers.push(this.inwardHandling.supplier);
        this.hasSupplierOrder = false;
      }
      if (this.inwardHandling.supplier_order) {
        this.products = await this.$store.dispatch('product/fetchBySupplierOrderId', this.inwardHandling.supplier_order.id);
      } else {
        this.products = await this.$store.dispatch('product/fetchBySupplierId', this.inwardHandling.supplier.id);
      }
    } else {
      this.inwardHandling.customer = this.customers[0];
      this.inwardHandling.currency = this.currencies[0];
      this.inwardHandling.storage = this.storages[0];
      this.inwardHandling.completed_at = moment()
      .format('Y-MM-DD');
    }
    this.loading = false;
  },
  methods: {
    async onSupplierChange() {
      this.$refs['productItems'].clearItems();
      this.loading = true;
      this.products = await this.$store.dispatch('product/fetchBySupplierId', this.inwardHandling.supplier.id);
      this.inwardHandling = {
        id: this.inwardHandling.id || null,
        completed_at: moment()
        .format('Y-MM-DD'),
        currency_id: this.currencies[0].id,
        currency: this.currencies[0],
        status: 'recorded',
        net_price: 0,
        gross_price: 0,
        order_items: [],
        storage: this.storages[0],
        supplier: this.inwardHandling.supplier,
        customer: this.customers[0],
        has_no_more_delivery: false,
      };
      this.loading = false;
      this.$refs['orderForm'].clearValidate(['reference_number']);
    },
    async searchSuppliers(query) {
      this.remoteLoading = true;
      if (!query) {
        this.suppliers = [];
      } else {
        const queryString = new URLSearchParams({
          name: query,
        });
        this.remoteSuppliers = await this.$store.dispatch('partner/search', '?' + queryString.toString());
      }
      this.remoteLoading = false;
    },

    onHasSupplierOrderChange(hasOrder) {
      this.$refs['productItems'].clearItems();
      if (!hasOrder) {
        this.inwardHandling = {
          id: this.inwardHandling.id,
          completed_at: moment()
          .format('Y-MM-DD'),
          currency_id: this.currencies[0].id,
          currency: this.currencies[0],
          status: 'recorded',
          net_price: 0,
          gross_price: 0,
          order_items: [],
          storage: this.storages[0],
          supplier: {},
          customer: this.customers[0],
        };
      } else {
        this.inwardHandling = {
          id: this.inwardHandling.id,
          completed_at: moment()
          .format('Y-MM-DD'),
          currency_id: this.currencies[0].id,
          currency: this.currencies[0],
          status: 'recorded',
          net_price: 0,
          gross_price: 0,
          order_items: [],
          storage: this.storages[0],
          supplier: {},
          customer: this.customers[0],
          has_no_more_delivery: false,
        };
        this.products = null;
      }
      this.$refs['orderForm'].clearValidate(['reference_number']);
    },
    async onSelectOrder(order) {
      const orderData = await axios.get('api/supplier-orders/' + order.id);
      this.$refs['productItems'].clearItems();
      this.productItemsLoading = true;
      this.inwardHandling.supplier_order = orderData;
      this.inwardHandling.remaining_inward_handlings = (await axios.get('api/supplier-orders/remaining-inward-items/' + this.inwardHandling.supplier_order.id)).map(item => ({
        ...item,
        max_count: item.count,
        key: Math.random().toString(36).substring(7)
      })).filter(item => item.count > 0);
      this.inwardHandling.storage = orderData.storage;
      this.inwardHandling.customer = orderData.customer;
      this.inwardHandling.currency = orderData.currency;
      this.inwardHandling.net_price = orderData.net_price;
      this.inwardHandling.gross_price = orderData.gross_price;
      this.inwardHandling.has_no_more_delivery = orderData.has_no_more_delivery;
      //this.inwardHandling.inward_handling_items = order.order_items.map(item => { ???????
      this.inwardHandling.inward_handling_items = this.inwardHandling.remaining_inward_handlings.map(item => {
        delete item.id;
        item.key = Math.random().toString(36).substring(7);
        item.max_count = item.count;
        return item;
      });
      this.products = await this.$store.dispatch('product/fetchBySupplierOrderId', order.id);
      this.productItemsLoading = false;
    },
    onPriceChange({ netPrice, grossPrice }) {
      this.inwardHandling.net_price = netPrice;
      this.inwardHandling.gross_price = grossPrice;
    },
    handleClose() {
      if(this.type === 'view') {
        return this.$router.replace('/processes/inward-handlings');
      }
      this.$confirm('Biztosan elveted a módosításokat?', {
        confirmButtonText: 'Igen',
        type: 'warning',
      })
      .then(() => {
        this.$router.replace('/processes/inward-handlings');
      })
      .catch(() => {
      });
    },
    async store() {
      this.$refs.orderForm.validate(async valid => {
        const expValid = this.$refs['productItems'].validateExpirationDates();
        const supplierValid = this.$refs['productItems'].validateSuppliers();
        if (!expValid || !supplierValid || !valid) {
          this.$messageWrapper.warning('Hibásan kitöltött form.');
          return;
        }
        if (valid) {
          this.inwardHandling.inward_handling_items = this.$refs['productItems'].getItems();
          if (!this.inwardHandling.inward_handling_items.length) {
            return this.$messageWrapper.warning('Minimum 1 termék hozzáadása kötelező.');
          } else if (!this.$refs['productItems'].validateProducts()) {
            return this.$messageWrapper.warning('Termékadatok hibásan vannak kitöltve.');
          }
          if (this.hasSupplierOrder && this.type === 'add') {
            const errorItems = [];
            this.inwardHandling.remaining_inward_handlings.forEach(item => {
              let count = item.count * item.product_pack_type.count_per_pack;
              this.inwardHandling.inward_handling_items.forEach(iItem => {
                if (iItem.product.id === item.product.id) {
                  count -= iItem.count * iItem.product_pack_type.count_per_pack;
                }
              });
              if (count < 0 && !item.product.is_pallet) {
                errorItems.push({
                  productName: item.product.name,
                  count,
                });
              }
            });
            if (errorItems && errorItems.length) {
              this.$messageWrapper.warning('A beszállítói megrendelésen szereplő mennyiségeknél több nem vételezhető be.');
              return;
            }
          }
          this.buttonLoading = true;
          this.inwardHandling.supplier_order = {
            id: this.inwardHandling.supplier_order && this.inwardHandling.supplier_order.id
          }
          this.inwardHandling.storage = {
            id: this.inwardHandling.storage && this.inwardHandling.storage.id
          }
          this.inwardHandling.supplier = {
            id: this.inwardHandling.supplier && this.inwardHandling.supplier.id
          }
          this.inwardHandling.customer = {
            id: this.inwardHandling.customer && this.inwardHandling.customer.id
          }
          this.inwardHandling.creator_user = {
            id: this.inwardHandling.creator_user && this.inwardHandling.creator_user.id
          }
          this.inwardHandling.remaining_inward_handlings = []
          this.inwardHandling.inward_handling_items = this.inwardHandling.inward_handling_items.map(item => {
            return {
              ...item,
              product: {
                id: item.product.id,
              },
              product_pack_type: {
                id: item.product_pack_type.id
              },
              supplier: {
                id: item.supplier_id
              },
            }
          })
          try {
            if (this.type === 'add') {
              await this.$store.dispatch('inwardHandling/create', this.inwardHandling);
            } else if (this.type === 'update') {
              await this.$store.dispatch('inwardHandling/update', this.inwardHandling);
            }
            this.$store.dispatch('inwardHandling/fetchAll');
            this.$router.replace('/processes/inward-handlings');
          } catch (e) {
          }
          this.buttonLoading = false;
        }
      });
    },
    openReceipt() {
      if (this.inwardHandling.receipt_file && this.inwardHandling.receipt_file.path) {
        window.open(this.inwardHandling.receipt_file.path);
      }
    },
    getStatus() {
      switch (this.inwardHandling.status) {
        case 'recorded':
          return {
            label: 'Rögzítve',
            tag: 'info',
          };
        case 'done':
          return {
            label: 'Kész',
            tag: 'success',
          };
      }
    },
  }
}
</script>

<style lang="scss">
  .price-sum {
    margin-top: 20px;
    text-align: right;
    font-size: 18px;
    > div {
      display: inline-block;
    }
  }
</style>
